import { LinearProgress } from '@mui/material'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useAxiosInterceptors } from '@/hooks/useAxiosInterceptors'
import { useStorageState } from '@/hooks/useStorageState'
import {
  getAppAuthContext,
  getAppAuthContextFunctions,
  getAppAuthInitialState,
  type IAppAuthContext,
  type IAppAuthState,
  sweepExpiredSessions,
  validateAppAuthState,
} from '@/services/Auth'
import {
  getAppBreadcrumbsContext,
  getAppBreadcrumbsContextFunctions,
  getAppBreadcrumbsInitialState,
  type IAppBreadcrumbsContext,
  type IAppBreadcrumbsState,
} from '@/services/Breadcrumbs'
import {
  getAppLocaleContext,
  getAppLocaleContextFunctions,
  getAppLocaleInitialState,
  type IAppLocaleContext,
  type IAppLocaleState,
  validateAppLocaleState,
} from '@/services/Locale'
import {
  getAppSettingsContext,
  getAppSettingsContextFunctions,
  getAppSettingsInitialState,
  type IAppSettingsContext,
  type IAppSettingsState,
} from '@/services/Settings'
import {
  getAppViewContext,
  getAppViewContextFunctions,
  getAppViewInitialState,
  type IAppViewContext,
  type IAppViewState,
} from '@/services/View'

// ///////////////////////
// MARK: CONTEXT
export const LocaleContext = createContext<IAppLocaleContext>(null as unknown as IAppLocaleContext)
LocaleContext.displayName = 'LocaleContext'
export const AuthContext = createContext<IAppAuthContext>(null as unknown as IAppAuthContext)
AuthContext.displayName = 'AuthContext'
export const ViewContext = createContext<IAppViewContext>(null as unknown as IAppViewContext)
ViewContext.displayName = 'ViewContext'
export const SettingsContext = createContext<IAppSettingsContext>(
  null as unknown as IAppSettingsContext,
)
SettingsContext.displayName = 'SettingsContext'
export const BreadcrumbsContext = createContext<IAppBreadcrumbsContext>(
  null as unknown as IAppBreadcrumbsContext,
)
BreadcrumbsContext.displayName = 'BreadcrumbsContext'

// ///////////////////////
// MARK: HOOK
export function useLocaleContext(): IAppLocaleContext {
  return useContext(LocaleContext)!
}
export function useAuthContext(): IAppAuthContext {
  return useContext(AuthContext)!
}
export function useViewContext(): IAppViewContext {
  return useContext(ViewContext)!
}
export function useSettingsContext(): IAppSettingsContext {
  return useContext(SettingsContext)!
}
export function useBreadcrumbsContext(): IAppBreadcrumbsContext {
  return useContext(BreadcrumbsContext)!
}

// ///////////////////////
// MARK: PROVIDER
export const GlobalProvider: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => {
  // ///////////////////////
  // LOCALE
  const [Locale, setLocale] = useStorageState<IAppLocaleState>(
    'local',
    'locale',
    getAppLocaleInitialState,
    validateAppLocaleState,
  )

  // ///////////////////////
  // AUTH
  const [Auth, setAuth] = useStorageState<IAppAuthState>(
    'local',
    'auth',
    getAppAuthInitialState,
    validateAppAuthState,
  )

  // ///////////////////////
  // VIEW
  const [View, setView] = useState<IAppViewState>(getAppViewInitialState)

  // ///////////////////////
  // SETTINGS
  const [Settings, setSettings] = useState<IAppSettingsState>(getAppSettingsInitialState)

  // ///////////////////////
  // BREADCRUMBS
  const [Breadcrumbs, setBreadcrumbs] = useState<IAppBreadcrumbsState>(
    getAppBreadcrumbsInitialState,
  )

  // ///////////////////////
  // MEMOS
  const LocaleContextValue = useMemo<IAppLocaleContext>(
    () => ({
      ...Locale,
      ...getAppLocaleContext(),
      ...getAppLocaleContextFunctions(Locale, setLocale),
    }),
    [Locale, setLocale],
  )

  const AuthContextValue = useMemo<IAppAuthContext>(
    () => ({
      ...Auth,
      ...getAppAuthContext(),
      ...getAppAuthContextFunctions(Auth, setAuth),
    }),
    [Auth, setAuth],
  )

  const ViewContextValue = useMemo<IAppViewContext>(
    () => ({
      ...View,
      ...getAppViewContext(),
      ...getAppViewContextFunctions(View, setView),
    }),
    [View],
  )

  const SettingsContextValue = useMemo<IAppSettingsContext>(
    () => ({
      ...Settings,
      ...getAppSettingsContext(),
      ...getAppSettingsContextFunctions(Settings, setSettings),
    }),
    [Settings],
  )

  const BreadcrumbsContextValue = useMemo<IAppBreadcrumbsContext>(
    () => ({
      ...Breadcrumbs,
      ...getAppBreadcrumbsContext(),
      ...getAppBreadcrumbsContextFunctions(Breadcrumbs, setBreadcrumbs),
    }),
    [Breadcrumbs],
  )

  const mounted = useAxiosInterceptors(AuthContextValue)

  useEffect(() => {
    const interval = setInterval(
      () => {
        setAuth(sweepExpiredSessions)
      },
      5 * 60 * 1000,
    )

    return () => clearInterval(interval)
  }, [setAuth])

  // ///////////////////////
  // RETURN
  if (mounted) {
    return (
      <LocaleContext.Provider value={LocaleContextValue}>
        <AuthContext.Provider value={AuthContextValue}>
          <ViewContext.Provider value={ViewContextValue}>
            <SettingsContext.Provider value={SettingsContextValue}>
              <BreadcrumbsContext.Provider value={BreadcrumbsContextValue}>
                {children}
              </BreadcrumbsContext.Provider>
            </SettingsContext.Provider>
          </ViewContext.Provider>
        </AuthContext.Provider>
      </LocaleContext.Provider>
    )
  }

  return <LinearProgress />
}
